// 視聴側の画面アノテーション (注釈描画) モジュール。
//
// 用途: viewer ユーザが視聴中の動画上に印を付けて「この部分」と指し示せる
// ようにする。同期は行わず viewer ローカルのみ (CLAUDE.md 7.2 のデータ
// チャネル不使用方針を維持)。
//
// 設計:
// - 各 <video> ごとに独立した <svg> オーバーレイを document.body 直下に
//   position:fixed で重ねる。位置・サイズは requestAnimationFrame で
//   video.getBoundingClientRect() に追従させる。
// - 座標は動画固有座標 (originalWidth × originalHeight) で保存し、
//   SVG の viewBox + preserveAspectRatio="xMidYMid meet" で letterbox 補正。
//   これによりレイアウト切替・ズーム・パン・自由配置のリサイズに関係なく
//   描画位置が動画ピクセル上で安定する。
// - 3D room レイアウトは壁が 3D 回転するため AABB な fixed オーバーレイでは
//   追従不可。setEnabled(false) で全オーバーレイ非表示にする。
//
// ツール: pen (自由筆記) / rect / circle。色: red / blue / yellow。
// ツール = "off" のときオーバーレイは pointer-events:none となり、下層の
// video クリック (swap) や stage-zoom のホイール・ドラッグに干渉しない。

export type Tool = "off" | "pen" | "rect" | "circle";
export type Color = "red" | "blue" | "yellow";

const SVG_NS = "http://www.w3.org/2000/svg";

const COLOR_HEX: Record<Color, string> = {
  red: "#ef4444",
  blue: "#3b82f6",
  yellow: "#facc15",
};

// 動画固有解像度に対する線幅比率。1280x720 で約 6.4px、1920x1080 で 9.6px。
const STROKE_RATIO = 0.005;
const MIN_STROKE = 3;

// シェイプ確定時の最小サイズ (動画固有座標)。これ未満は誤クリックとして破棄。
const MIN_SHAPE_PX = 4;

type ShapeId = string;

type PenShape = {
  id: ShapeId;
  type: "pen";
  color: Color;
  points: Array<[number, number]>;
};
type RectShape = {
  id: ShapeId;
  type: "rect";
  color: Color;
  x: number;
  y: number;
  w: number;
  h: number;
};
type CircleShape = {
  id: ShapeId;
  type: "circle";
  color: Color;
  cx: number;
  cy: number;
  rx: number;
  ry: number;
};
type Shape = PenShape | RectShape | CircleShape;

type Entry = {
  video: HTMLVideoElement;
  svg: SVGSVGElement;
  // 各 video の左下に重ねるブランド透かし。Pro プランで非表示化する想定の "種"。
  // shapes/shapeEls とは独立して管理し、clearAll / undo の対象外。
  watermark: SVGTextElement;
  shapes: Map<ShapeId, Shape>;
  shapeEls: Map<ShapeId, SVGElement>;
  originalW: number;
  originalH: number;
};

type Drag = {
  entry: Entry;
  id: ShapeId;
  origin: { x: number; y: number };
  pointerId: number;
  moved: boolean;
};

const entries = new Map<HTMLVideoElement, Entry>();
let tool: Tool = "off";
let color: Color = "red";
let enabled = true; // 3D 時は false にして全オーバーレイ非表示
let rafId = 0;
let nextShapeId = 1;
let drag: Drag | null = null;
let onHistoryChangeCb: (() => void) | null = null;
let hostEl: HTMLElement | null = null;

// 確定したシェイプの追加順を保持する Undo スタック。
// pointerup で破棄判定を通過した瞬間に push し、undo() で pop した上で
// 対応シェイプを削除する。Clear 時は履歴ごとリセット。
const history: Array<{ video: HTMLVideoElement; shapeId: ShapeId }> = [];

export function init(deps: { host: HTMLElement }): void {
  hostEl = deps.host;
}

function makeId(): ShapeId {
  nextShapeId += 1;
  return `s${nextShapeId}`;
}

function strokeWidth(entry: Entry): number {
  const base = Math.max(entry.originalW, entry.originalH);
  return Math.max(MIN_STROKE, base * STROKE_RATIO);
}

function isShapeTooSmall(shape: Shape): boolean {
  if (shape.type === "pen") return shape.points.length < 2;
  if (shape.type === "rect") return shape.w < MIN_SHAPE_PX || shape.h < MIN_SHAPE_PX;
  return shape.rx < MIN_SHAPE_PX / 2 || shape.ry < MIN_SHAPE_PX / 2;
}

function clientToSvgPoint(
  svg: SVGSVGElement,
  clientX: number,
  clientY: number,
): { x: number; y: number } {
  const pt = svg.createSVGPoint();
  pt.x = clientX;
  pt.y = clientY;
  const ctm = svg.getScreenCTM();
  if (!ctm) return { x: 0, y: 0 };
  const p = pt.matrixTransform(ctm.inverse());
  return { x: p.x, y: p.y };
}

function createShapeElement(shape: Shape, entry: Entry): SVGElement {
  const sw = strokeWidth(entry);
  const stroke = COLOR_HEX[shape.color];
  if (shape.type === "pen") {
    const el = document.createElementNS(SVG_NS, "polyline");
    el.setAttribute("fill", "none");
    el.setAttribute("stroke", stroke);
    el.setAttribute("stroke-width", String(sw));
    el.setAttribute("stroke-linecap", "round");
    el.setAttribute("stroke-linejoin", "round");
    return el;
  }
  if (shape.type === "rect") {
    const el = document.createElementNS(SVG_NS, "rect");
    el.setAttribute("fill", "none");
    el.setAttribute("stroke", stroke);
    el.setAttribute("stroke-width", String(sw));
    return el;
  }
  const el = document.createElementNS(SVG_NS, "ellipse");
  el.setAttribute("fill", "none");
  el.setAttribute("stroke", stroke);
  el.setAttribute("stroke-width", String(sw));
  return el;
}

function updateShapeElement(el: SVGElement, shape: Shape): void {
  if (shape.type === "pen") {
    const pts = shape.points.map((p) => `${p[0]},${p[1]}`).join(" ");
    el.setAttribute("points", pts);
    return;
  }
  if (shape.type === "rect") {
    el.setAttribute("x", String(shape.x));
    el.setAttribute("y", String(shape.y));
    el.setAttribute("width", String(shape.w));
    el.setAttribute("height", String(shape.h));
    return;
  }
  el.setAttribute("cx", String(shape.cx));
  el.setAttribute("cy", String(shape.cy));
  el.setAttribute("rx", String(shape.rx));
  el.setAttribute("ry", String(shape.ry));
}

function syncOverlay(entry: Entry): void {
  const rect = entry.video.getBoundingClientRect();
  // 0 サイズや非表示状態 (display:none などで rect が 0) は overlay も非表示
  if (rect.width === 0 || rect.height === 0 || !enabled) {
    entry.svg.style.display = "none";
    return;
  }
  entry.svg.style.display = "";
  // 親要素の transform 起点 (左上) からの差分で配置するため fixed + topleft 配置
  entry.svg.style.left = `${rect.left}px`;
  entry.svg.style.top = `${rect.top}px`;
  entry.svg.style.width = `${rect.width}px`;
  entry.svg.style.height = `${rect.height}px`;
  // ツール選択中のみイベント受け取り。Off なら下層に通す。
  entry.svg.style.pointerEvents = tool === "off" ? "none" : "auto";
  // 透かしを画面 px ターゲットに維持
  syncWatermark(entry, rect.width, rect.height);
}

function tick(): void {
  for (const entry of entries.values()) {
    syncOverlay(entry);
  }
  rafId = requestAnimationFrame(tick);
}

function ensureRaf(): void {
  if (rafId === 0) rafId = requestAnimationFrame(tick);
}

function stopRaf(): void {
  if (rafId !== 0) cancelAnimationFrame(rafId);
  rafId = 0;
}

function onPointerDown(e: PointerEvent): void {
  if (tool === "off") return;
  const svg = e.currentTarget as SVGSVGElement;
  let entry: Entry | null = null;
  for (const ent of entries.values()) {
    if (ent.svg === svg) {
      entry = ent;
      break;
    }
  }
  if (!entry) return;
  if (e.button !== 0) return;
  e.preventDefault();
  e.stopPropagation();
  const pt = clientToSvgPoint(svg, e.clientX, e.clientY);
  const id = makeId();
  let shape: Shape;
  if (tool === "pen") {
    shape = { id, type: "pen", color, points: [[pt.x, pt.y]] };
  } else if (tool === "rect") {
    shape = { id, type: "rect", color, x: pt.x, y: pt.y, w: 0, h: 0 };
  } else {
    shape = { id, type: "circle", color, cx: pt.x, cy: pt.y, rx: 0, ry: 0 };
  }
  entry.shapes.set(id, shape);
  const el = createShapeElement(shape, entry);
  updateShapeElement(el, shape);
  entry.svg.appendChild(el);
  entry.shapeEls.set(id, el);
  try {
    svg.setPointerCapture(e.pointerId);
  } catch {
    // ignore
  }
  drag = { entry, id, origin: { x: pt.x, y: pt.y }, pointerId: e.pointerId, moved: false };
}

function onPointerMove(e: PointerEvent): void {
  if (!drag || e.pointerId !== drag.pointerId) return;
  const pt = clientToSvgPoint(drag.entry.svg, e.clientX, e.clientY);
  const shape = drag.entry.shapes.get(drag.id);
  const el = drag.entry.shapeEls.get(drag.id);
  if (!shape || !el) return;
  if (shape.type === "pen") {
    shape.points.push([pt.x, pt.y]);
  } else if (shape.type === "rect") {
    shape.x = Math.min(drag.origin.x, pt.x);
    shape.y = Math.min(drag.origin.y, pt.y);
    shape.w = Math.abs(pt.x - drag.origin.x);
    shape.h = Math.abs(pt.y - drag.origin.y);
  } else {
    shape.cx = (drag.origin.x + pt.x) / 2;
    shape.cy = (drag.origin.y + pt.y) / 2;
    shape.rx = Math.abs(pt.x - drag.origin.x) / 2;
    shape.ry = Math.abs(pt.y - drag.origin.y) / 2;
  }
  drag.moved = true;
  updateShapeElement(el, shape);
}

function onPointerUp(e: PointerEvent): void {
  if (!drag || e.pointerId !== drag.pointerId) return;
  try {
    drag.entry.svg.releasePointerCapture(e.pointerId);
  } catch {
    // ignore
  }
  const shape = drag.entry.shapes.get(drag.id);
  // 動かさず終わったクリック、または極小シェイプは破棄
  if (shape && (!drag.moved || isShapeTooSmall(shape))) {
    drag.entry.shapes.delete(drag.id);
    drag.entry.shapeEls.get(drag.id)?.remove();
    drag.entry.shapeEls.delete(drag.id);
  } else if (shape) {
    // 確定したので Undo 履歴に積む
    history.push({ video: drag.entry.video, shapeId: drag.id });
    if (onHistoryChangeCb) onHistoryChangeCb();
  }
  drag = null;
}

// 透かしのスクリーン上の文字サイズは、video の **表示** サイズ (小さい方の辺) の
// 約 2.2% に比例させる。ホストの配信解像度には依存させない。
// レイアウト切替で video セルが縮めば透かしも縮み、フルスクリーンなら大きく出る。
// 極端なケースに備えて floor / ceiling を入れる。
const WATERMARK_RATIO = 0.0264;
const WATERMARK_MIN_PX = 15;
const WATERMARK_MAX_PX = 22;

function createOverlay(
  originalW: number,
  originalH: number,
): { svg: SVGSVGElement; watermark: SVGTextElement } {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", `0 0 ${originalW} ${originalH}`);
  svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
  svg.classList.add("annot-overlay");
  svg.style.position = "fixed";
  svg.style.left = "0";
  svg.style.top = "0";
  svg.style.pointerEvents = "none";
  // .stage 内でツールバー (z-index:2) より背面、video-grid より前面
  svg.style.zIndex = "1";
  svg.addEventListener("pointerdown", onPointerDown);
  svg.addEventListener("pointermove", onPointerMove);
  svg.addEventListener("pointerup", onPointerUp);
  svg.addEventListener("pointercancel", onPointerUp);

  // 左下のブランド透かし。位置と font-size は syncOverlay が毎フレーム
  // viewBox -> screen px のスケール逆算で決定するので、ここでは text 内容と
  // baseline 属性だけ設定しておく。pointer-events: none は CSS で個別指定し、
  // SVG の pointerEvents が auto (描画中) になっても透かし自体はクリック透過。
  const watermark = document.createElementNS(SVG_NS, "text") as SVGTextElement;
  watermark.classList.add("annot-watermark");
  // 右下配置。end にすると x が文字の右端を指すので、video の右端から
  // insetX 引いた位置を x に渡すだけで右揃えになる。
  watermark.setAttribute("text-anchor", "end");
  // text-after-edge にすると y は文字の下端を指す。bottom 起点で位置決めしやすい。
  watermark.setAttribute("dominant-baseline", "text-after-edge");
  watermark.textContent = "WinCast";
  svg.appendChild(watermark);

  return { svg, watermark };
}

// SVG viewBox -> 画面 px のスケールから、透かしの x/y/font-size を screen px ターゲットに
// 合わせて user-unit に逆算する。preserveAspectRatio="xMidYMid meet" なので
// X と Y のスケールは等しく、displayed/viewBox の小さい方が共通スケールになる。
function syncWatermark(
  entry: Entry,
  displayW: number,
  displayH: number,
): void {
  const scale = Math.min(
    displayW / entry.originalW,
    displayH / entry.originalH,
  );
  if (!Number.isFinite(scale) || scale <= 0) return;
  // スクリーン上の目標サイズ (表示サイズの短辺基準)
  const referenceDim = Math.min(displayW, displayH);
  const fontSizeScreenPx = Math.min(
    WATERMARK_MAX_PX,
    Math.max(WATERMARK_MIN_PX, referenceDim * WATERMARK_RATIO),
  );
  // viewBox user-unit に逆算
  const fontSize = fontSizeScreenPx / scale;
  // 余白は文字サイズ相対 (em 風)。文字サイズに連動して破綻しない。
  const insetX = fontSize * 0.7;
  const insetY = fontSize * 0.5;
  entry.watermark.setAttribute("font-size", String(fontSize));
  // text-anchor="end" なので x は文字右端 = video 右端 - insetX
  entry.watermark.setAttribute("x", String(entry.originalW - insetX));
  entry.watermark.setAttribute("y", String(entry.originalH - insetY));
}

export function attachVideo(
  video: HTMLVideoElement,
  originalW: number,
  originalH: number,
): void {
  if (entries.has(video)) return;
  // 解像度未確定 (originalWidth = 0) でも仮値で SVG を作っておき、判明したら更新
  const w = originalW > 0 ? originalW : 1280;
  const h = originalH > 0 ? originalH : 720;
  const { svg, watermark } = createOverlay(w, h);
  // host (.stage) に append すると stage-overlay (toolbar) の z-index と
  // 同じスタッキングコンテキストに入り、toolbar が SVG の上に正しく出る。
  (hostEl ?? document.body).appendChild(svg);
  const entry: Entry = {
    video,
    svg,
    watermark,
    shapes: new Map(),
    shapeEls: new Map(),
    originalW: w,
    originalH: h,
  };
  entries.set(video, entry);
  ensureRaf();
  syncOverlay(entry);
}

export function updateOriginalSize(
  video: HTMLVideoElement,
  originalW: number,
  originalH: number,
): void {
  const entry = entries.get(video);
  if (!entry) return;
  if (originalW <= 0 || originalH <= 0) return;
  // 既に近い値なら更新しない (浮動小数誤差を避ける)
  if (
    Math.abs(entry.originalW - originalW) < 0.5 &&
    Math.abs(entry.originalH - originalH) < 0.5
  ) {
    return;
  }
  // 既存シェイプは旧座標系で保存されているのでスケール変換する
  const sx = originalW / entry.originalW;
  const sy = originalH / entry.originalH;
  for (const shape of entry.shapes.values()) {
    if (shape.type === "pen") {
      shape.points = shape.points.map(([x, y]) => [x * sx, y * sy]);
    } else if (shape.type === "rect") {
      shape.x *= sx;
      shape.y *= sy;
      shape.w *= sx;
      shape.h *= sy;
    } else {
      shape.cx *= sx;
      shape.cy *= sy;
      shape.rx *= sx;
      shape.ry *= sy;
    }
    const el = entry.shapeEls.get(shape.id);
    if (el) updateShapeElement(el, shape);
  }
  entry.originalW = originalW;
  entry.originalH = originalH;
  entry.svg.setAttribute("viewBox", `0 0 ${originalW} ${originalH}`);
  // 透かしの位置・サイズは次の syncOverlay tick (~16ms 以内) で再計算される
  // 線幅も更新
  for (const [id, shape] of entry.shapes) {
    const el = entry.shapeEls.get(id);
    if (el) el.setAttribute("stroke-width", String(strokeWidth(entry)));
  }
}

export function detachVideo(video: HTMLVideoElement): void {
  const entry = entries.get(video);
  if (!entry) return;
  entry.svg.remove();
  entries.delete(video);
  // 切り離された video を参照する履歴は実行時に undo() がスキップするので
  // ここで明示削除はせず、空エントリの遅延掃除に任せる。
  if (entries.size === 0) stopRaf();
}

export function setTool(t: Tool): void {
  tool = t;
  // off 切替時はドラッグ中シェイプを確定 (破棄判定経由)
  if (t === "off" && drag) {
    onPointerUp(new PointerEvent("pointerup", { pointerId: drag.pointerId }));
  }
  // pointerEvents を全 overlay に反映 (rAF を待たず即反映)
  for (const entry of entries.values()) {
    entry.svg.style.pointerEvents = tool === "off" ? "none" : "auto";
  }
}

export function setColor(c: Color): void {
  color = c;
}

export function getTool(): Tool {
  return tool;
}

export function getColor(): Color {
  return color;
}

export function setEnabled(e: boolean): void {
  enabled = e;
}

export function clearAll(): void {
  for (const entry of entries.values()) {
    for (const el of entry.shapeEls.values()) el.remove();
    entry.shapes.clear();
    entry.shapeEls.clear();
  }
  history.length = 0;
}

// 直前 1 シェイプを取り消す。video / shapeId 両方が存在する場合のみ削除。
// (途中で video が detach された場合は履歴を無効としてスキップ)
export function undo(): boolean {
  while (history.length > 0) {
    const last = history.pop();
    if (!last) return false;
    const entry = entries.get(last.video);
    if (!entry) continue;
    const el = entry.shapeEls.get(last.shapeId);
    if (!el) continue;
    el.remove();
    entry.shapes.delete(last.shapeId);
    entry.shapeEls.delete(last.shapeId);
    return true;
  }
  return false;
}

export function canUndo(): boolean {
  return history.length > 0;
}

export function onHistoryChange(cb: () => void): void {
  onHistoryChangeCb = cb;
}
